import type { PublicationOption, RegionOption } from "./types.js";
import styles from "../PipelineBrowser.module.css";

type OptionsState<T> = {
  options: T[];
  loading: boolean;
  error: string | null;
};

type SideProps = {
  title: string;
  hint: string;
  publications: OptionsState<PublicationOption>;
  regions: OptionsState<RegionOption>;
  publicationId: number | null;
  regionId: number | null;
  onPublicationChange: (id: number | null) => void;
  onRegionChange: (id: number | null) => void;
  disabled?: boolean;
};

function PublicationRegionSide({
  title,
  hint,
  publications,
  regions,
  publicationId,
  regionId,
  onPublicationChange,
  onRegionChange,
  disabled,
}: SideProps) {
  return (
    <div className={styles.filterField}>
      <span className={styles.filterLabel}>{title}</span>
      <select
        className={styles.filterSelect}
        value={publicationId ?? ""}
        disabled={disabled || publications.loading}
        onChange={e => onPublicationChange(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">
          {publications.loading ? "Loading publications…" : "Select publication"}
        </option>
        {publications.options.map(p => (
          <option key={p.id} value={p.id}>
            {p.name}
          </option>
        ))}
      </select>
      <select
        className={styles.filterSelect}
        value={regionId ?? ""}
        disabled={disabled || publicationId == null || regions.loading}
        onChange={e => onRegionChange(e.target.value ? Number(e.target.value) : null)}
      >
        <option value="">{regions.loading ? "Loading regions…" : "Select region"}</option>
        {regions.options.map(r => (
          <option key={r.id} value={r.id}>
            {r.name}
          </option>
        ))}
      </select>
      <span className={styles.mappingHint}>{hint}</span>
      {(publications.error || regions.error) && (
        <span className={styles.mappingHint}>Error: {publications.error || regions.error}</span>
      )}
    </div>
  );
}

type ContextSelectorsProps = {
  edition: string;
  onEditionChange: (edition: string) => void;
  source: Omit<SideProps, "title" | "hint">;
  dest: Omit<SideProps, "title" | "hint">;
  disabled?: boolean;
};

// Edition input + Source and Destination publication/region selectors.
export function ContextSelectors({
  edition,
  onEditionChange,
  source,
  dest,
  disabled,
}: ContextSelectorsProps) {
  return (
    <div className={styles.contentSection}>
      <div className={styles.contentSectionTitle}>Context</div>
      <div className={styles.filters}>
        <div className={styles.filterField}>
          <span className={styles.filterLabel}>Edition</span>
          <input
            className={styles.filterInput}
            value={edition}
            disabled={disabled}
            placeholder="e.g. 2026"
            onChange={e => onEditionChange(e.target.value)}
          />
        </div>
        <PublicationRegionSide
          title="Source"
          hint="Select the EMEA publication and France region (ID changes every year)"
          {...source}
          disabled={disabled}
        />
        <PublicationRegionSide
          title="Destination"
          hint="Select the Paris publication and Paris region (ID changes every year)"
          {...dest}
          disabled={disabled}
        />
      </div>
    </div>
  );
}
